import { useEffect, useMemo, useRef } from 'react';

import { useQueries, useQueryClient } from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type {
  ConnectorInvocationStatus,
  InvocationStatusType,
} from '@/types/core/connector-invocation-status';
import type { UUID } from '@/utils/uuid';

import { connectorQueryKeys } from './query-keys';

import { RUN_HISTORY_COUNT } from '@/constants/run-history';

const RUN_COUNT = RUN_HISTORY_COUNT;
/** A feature needs attention when this fraction of its last 10 runs failed (30%). */
const ATTENTION_THRESHOLD = 0.3;

export type RunOutcome = 'passed' | 'failed' | 'empty';

export interface RunPositionMetrics {
  collected: number;
  changed: number;
  deleted: number;
  failed: number;
}

export interface FeatureRunHistory {
  /** 10 slots, oldest → newest, right-aligned (empty slots on the left) */
  runs: RunOutcome[];
  passed: number;
  failed: number;
  /** True when ≥30% of the last 10 completed runs failed */
  needsAttention: boolean;
}

export interface ConnectorRunHistory {
  /** Aggregate 10-slot run history across all features */
  runs: RunOutcome[];
  passed: number;
  failed: number;
  /** True if any feature meets the attention threshold (≥30% of last 10 runs failed) */
  needsAttention: boolean;
  /** Per-feature run history keyed by featureId */
  featureRuns: Map<UUID, FeatureRunHistory>;
  /** Per-run-position entity metrics (collected/changed/deleted), 10 slots oldest → newest */
  runMetrics: RunPositionMetrics[];
  isLoading: boolean;
}

const TERMINAL: Set<InvocationStatusType> = new Set([
  'completed',
  'failed',
  'stopped',
]);

const ACTIVE: Set<InvocationStatusType> = new Set([
  'queued',
  'requested',
  'running',
  'reconciling',
  'waiting',
  'updating',
]);

/**
 * Fetches the last 10 completed runs across all collector features for a single
 * connector and returns a per-slot outcome array for rendering a run history strip.
 *
 * Query keys intentionally match those used in useConnectorActivityBuckets so
 * results are served from cache when the activity chart is also mounted.
 *
 * When `currentStatuses` is provided, the hook automatically refetches when any
 * feature transitions from an active status to a terminal status (i.e. a run
 * just finished) so the history strip updates without a page refresh.
 */
export function useConnectorRunHistory(
  tenantId: UUID | undefined,
  connectorId: UUID | undefined,
  featureIds: UUID[],
  currentStatuses?: Record<UUID, ConnectorInvocationStatus>
): ConnectorRunHistory {
  const qc = useQueryClient();

  // Detect when a new run completes and invalidate run history queries.
  // We track both status and runSequence because a run may complete faster
  // than the poll interval — in that case both the previous and current status
  // are 'completed', so the active→terminal transition is never observed.
  // Watching the sequence number catches this case too.
  const prevStatusesRef = useRef<
    Record<UUID, { status: InvocationStatusType; seq?: number }>
  >({});
  useEffect(() => {
    if (!currentStatuses) return;

    const prev = prevStatusesRef.current;
    let shouldInvalidate = false;

    for (const [fid, s] of Object.entries(currentStatuses)) {
      const prevEntry = prev[fid as UUID];
      // Normal case: observed the active → terminal transition.
      if (
        prevEntry?.status &&
        ACTIVE.has(prevEntry.status) &&
        TERMINAL.has(s.status)
      ) {
        shouldInvalidate = true;
        break;
      }
      // Fast-completion case: run finished before we polled the active state.
      // Sequence advancement is authoritative even if the latest observed
      // status is already the next active run (queued/requested/running).
      if (
        s.runSequence != null &&
        prevEntry?.seq != null &&
        s.runSequence > prevEntry.seq
      ) {
        shouldInvalidate = true;
        break;
      }
    }

    // Update ref for next comparison.
    const next: Record<UUID, { status: InvocationStatusType; seq?: number }> =
      {};
    for (const [fid, s] of Object.entries(currentStatuses)) {
      next[fid as UUID] = { status: s.status, seq: s.runSequence };
    }
    prevStatusesRef.current = next;

    if (shouldInvalidate) {
      qc.invalidateQueries({
        queryKey: ['invocation-statuses', tenantId, connectorId],
        refetchType: 'all',
      });
    }
  }, [currentStatuses, qc, tenantId, connectorId]);
  const invocationResults = useQueries({
    queries: featureIds.map((featureId) => ({
      queryKey: connectorQueryKeys.invocationStatuses(
        tenantId,
        connectorId,
        featureId,
        RUN_COUNT + 1
      ),
      queryFn: () =>
        connectorService.listConnectorInvocationStatuses(
          tenantId!,
          connectorId!,
          featureId,
          { limit: RUN_COUNT + 1 }
        ),
      enabled: !!tenantId && !!connectorId && featureIds.length > 0,
      refetchInterval: 15_000,
    })),
  });

  const isLoading = invocationResults.some((r) => r.isLoading);

  return useMemo(() => {
    // Per run-position: aggregate outcome votes across all features
    const votes = Array.from({ length: RUN_COUNT }, () => ({
      passed: 0,
      failed: 0,
    }));

    // Per run-position: aggregate entity metrics across all features
    const positionMetrics = Array.from({ length: RUN_COUNT }, () => ({
      collected: 0,
      changed: 0,
      deleted: 0,
      failed: 0,
    }));

    const featureRuns = new Map<UUID, FeatureRunHistory>();

    for (let qi = 0; qi < invocationResults.length; qi++) {
      const result = invocationResults[qi];
      const featureId = featureIds[qi];
      const items = result.data?.items;

      if (!items || items.length === 0) continue;

      // Skip the first item only when it represents an in-flight run (active
      // status). When the most recent item is terminal it IS part of history
      // — this handles the window after a run completes but before the backend
      // auto-queues the next one.
      const skipFirst = items.length > 0 && !TERMINAL.has(items[0].status);
      const recent = skipFirst
        ? items.slice(1, RUN_COUNT + 1)
        : items.slice(0, RUN_COUNT);
      const offset = RUN_COUNT - recent.length;

      const featureSlots: RunOutcome[] = Array(RUN_COUNT).fill('empty');

      for (let i = recent.length - 1; i >= 0; i--) {
        const run = recent[i];
        if (!TERMINAL.has(run.status)) continue;
        const pos = offset + (recent.length - 1 - i);
        if (run.status === 'completed') {
          votes[pos].passed++;
          featureSlots[pos] = 'passed';
        } else if (run.status === 'failed') {
          votes[pos].failed++;
          featureSlots[pos] = 'failed';
          positionMetrics[pos].failed++;
        }

        // Accumulate entity metrics regardless of terminal outcome
        const m = positionMetrics[pos];
        const c =
          run.metrics?.collection?.entitiesCollected ?? run.processedCount;
        if (c != null) m.collected += c;
        const u = run.metrics?.reconciliation?.catalogApply?.upsertsApplied;
        if (u != null) m.changed += u;
        const d = run.metrics?.reconciliation?.catalogApply?.deletesApplied;
        if (d != null) m.deleted += d;
      }

      const fPassed = featureSlots.filter((r) => r === 'passed').length;
      const fFailed = featureSlots.filter((r) => r === 'failed').length;
      const completedRuns = fPassed + fFailed;
      featureRuns.set(featureId, {
        runs: featureSlots,
        passed: fPassed,
        failed: fFailed,
        needsAttention:
          completedRuns > 0 && fFailed / completedRuns >= ATTENTION_THRESHOLD,
      });
    }

    const runs: RunOutcome[] = votes.map((v) => {
      if (v.failed > v.passed) return 'failed';
      if (v.passed > 0) return 'passed';
      return 'empty';
    });

    const passed = runs.filter((r) => r === 'passed').length;
    const failed = runs.filter((r) => r === 'failed').length;
    // "needs attention" = at least one failure in the last 3 runs
    const needsAttention = runs.slice(-3).some((r) => r === 'failed');

    return {
      runs,
      passed,
      failed,
      needsAttention,
      featureRuns,
      runMetrics: positionMetrics,
      isLoading,
    };
  }, [invocationResults, featureIds, isLoading]);
}
